import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../../context/CartContext';
import { siteContent } from '../../data/products';
import {
  calculateCartSummary,
  earliestOrderDate,
  formatCartValue,
  formatINR,
  normalizeIndianPhone
} from '../../utils/commerce';
import { buildCartOrderMessage, buildWhatsAppUrl } from '../../utils/whatsapp';
import { saveOrderEnquiry } from '../../utils/orderEnquiry';
import Icon from '../Icon/Icon';
import './Cart.css';

const drawerSpring = { type: 'spring', stiffness: 300, damping: 32 };
const FOCUSABLE = 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const Cart = () => {
  const { detailed, count, add, decrement, remove, clear, open, setOpen } = useCart();
  const drawerRef = useRef(null);
  const closeRef = useRef(null);
  const emptyActionRef = useRef(null);
  const clearTriggerRef = useRef(null);
  const keepItemsRef = useRef(null);
  const previousItemTypes = useRef(detailed.length);
  const [confirmClear, setConfirmClear] = useState(false);
  const [checkoutFallback, setCheckoutFallback] = useState('');
  const [checkoutStep, setCheckoutStep] = useState(false);
  const [checkoutDetails, setCheckoutDetails] = useState(() => ({
    name: '',
    phone: '',
    fulfillment: 'delivery',
    area: '',
    address: '',
    date: '',
    time: siteContent.ordering.timeSlots[0] || 'Flexible',
    eggless: false,
    notes: ''
  }));
  const [checkoutErrors, setCheckoutErrors] = useState({});
  const [enquiryStatus, setEnquiryStatus] = useState('');
  const cartSummary = useMemo(() => calculateCartSummary(detailed), [detailed]);
  const hasQuoteItems = cartSummary.quoteQuantity > 0;
  const cartValueLabel = formatCartValue(cartSummary);
  const earliestDate = earliestOrderDate(detailed.map(({ product }) => product));

  // Lock body scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  useEffect(() => {
    if (!open || detailed.length === 0) setConfirmClear(false);
    if (!open) setCheckoutStep(false);
    if (detailed.length === 0) {
      setCheckoutStep(false);
      setCheckoutErrors({});
    }
  }, [detailed.length, open]);

  useEffect(() => {
    setCheckoutFallback('');
  }, [checkoutDetails, detailed]);

  useEffect(() => {
    if (!open || !checkoutStep) return;
    window.requestAnimationFrame(() => {
      drawerRef.current?.querySelector('[name="name"]')?.focus();
    });
  }, [checkoutStep, open]);

  useEffect(() => {
    if (open && previousItemTypes.current > 0 && detailed.length === 0) {
      window.requestAnimationFrame(() => {
        (emptyActionRef.current || closeRef.current)?.focus();
      });
    }
    previousItemTypes.current = detailed.length;
  }, [detailed.length, open]);

  useEffect(() => {
    if (open && confirmClear) {
      window.requestAnimationFrame(() => keepItemsRef.current?.focus());
    }
  }, [confirmClear, open]);

  /* The floating cart bar is a 60px-tall slab pinned to the bottom centre, and
   * on a 375px screen it completely swallows the WhatsApp float sitting at
   * bottom-right. Flag it on <body> so the floats can step up out of the way. */
  useEffect(() => {
    document.body.classList.toggle('has-cart-bar', count > 0 && !open);
    return () => document.body.classList.remove('has-cart-bar');
  }, [count, open]);

  /* The drawer is a modal: it has to take focus, keep Tab inside itself, close
   * on Escape, and hand focus back to whatever opened it. */
  useEffect(() => {
    if (!open) return undefined;
    const opener = document.activeElement;
    closeRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab') return;
      const nodes = drawerRef.current?.querySelectorAll(FOCUSABLE);
      if (!nodes || !nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, [open, setOpen]);

  const checkoutMessage = useMemo(() => buildCartOrderMessage({
    brandName: siteContent.brand.name,
    items: detailed,
    pricedTotal: cartSummary.pricedTotal,
    quoteQuantity: cartSummary.quoteQuantity,
    customer: { name: checkoutDetails.name, phone: checkoutDetails.phone },
    fulfillment: {
      method: checkoutDetails.fulfillment,
      area: checkoutDetails.area,
      address: checkoutDetails.address
    },
    neededBy: checkoutDetails.date,
    preferredTime: checkoutDetails.time,
    eggless: checkoutDetails.eggless,
    notes: checkoutDetails.notes,
    pickupMessage: siteContent.ordering.pickupMessage
  }), [cartSummary, checkoutDetails, detailed]);

  const updateCheckout = (event) => {
    const { name, type, checked, value } = event.target;
    setCheckoutDetails((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
    setCheckoutErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validateCheckout = () => {
    const next = {};
    if (checkoutDetails.name.trim().length < 2) next.name = 'Please enter your name.';
    if (!normalizeIndianPhone(checkoutDetails.phone)) next.phone = 'Enter a valid Indian mobile number.';
    if (checkoutDetails.date && checkoutDetails.date < earliestDate) {
      next.date = `Your cart needs more notice. Choose ${earliestDate} or later.`;
    }
    if (checkoutDetails.fulfillment === 'delivery') {
      if (checkoutDetails.area.trim().length < 2) next.area = 'Enter your delivery area.';
      if (checkoutDetails.address.trim().length < 8) next.address = 'Add a complete delivery address.';
    }
    return next;
  };

  const checkoutCart = (event) => {
    event.preventDefault();
    if (!detailed.length) return;
    const nextErrors = validateCheckout();
    setCheckoutErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      drawerRef.current?.querySelector(`[name="${firstError}"]`)?.focus();
      return;
    }

    setEnquiryStatus('Saving your enquiry…');
    void saveOrderEnquiry({
        customer_name: checkoutDetails.name.trim().slice(0, 120),
        customer_phone: normalizeIndianPhone(checkoutDetails.phone),
        summary: checkoutMessage.slice(0, 2000),
        order_items: detailed.map(({ product, qty, lineTotal }) => ({
          product_id: product.id,
          product_name: product.name,
          variant_id: product.variantId || null,
          quantity: qty,
          unit_price: product.price,
          line_total: lineTotal,
          price_on_request: product.price == null
        })),
        fulfilment: {
          method: checkoutDetails.fulfillment,
          area: checkoutDetails.area.trim(),
          address: checkoutDetails.address.trim(),
          preferred_time: checkoutDetails.time,
          eggless: checkoutDetails.eggless
        },
        requested_for: checkoutDetails.date || null,
        notes: checkoutDetails.notes.trim().slice(0, 2000),
        source: 'storefront'
      }).then((result) => setEnquiryStatus(result.saved
        ? 'Enquiry saved. Send your WhatsApp message so we can confirm your order.'
        : 'Please send the WhatsApp message to complete your enquiry.'));
    const url = buildWhatsAppUrl(siteContent.contact.whatsappNumber, checkoutMessage);
    const opened = window.open(url, '_blank');
    if (opened) {
      opened.opener = null;
      setCheckoutFallback('');
    } else {
      setCheckoutFallback(url);
    }
  };

  const clearCart = () => {
    clear();
    setConfirmClear(false);
  };

  return (
    <>
      {/* Floating "view cart" bar — appears only when there are items */}
      <AnimatePresence>
        {count > 0 && !open && (
          <motion.button
            type="button"
            className="cart-bar"
            aria-label={`View your order, ${count} ${count === 1 ? 'item' : 'items'}, ${cartValueLabel}`}
            initial={{ y: 130, x: '-50%', opacity: 0 }}
            animate={{ y: 0, x: '-50%', opacity: 1 }}
            exit={{ y: 130, x: '-50%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            onClick={() => setOpen(true)}
          >
            <div className="cart-bar-left">
              <motion.span
                key={count}
                className="cart-bar-count"
                initial={{ scale: 0.6 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 480, damping: 16 }}
              >
                {count}
              </motion.span>
              <div className="cart-bar-copy">
                <strong>{count === 1 ? '1 item' : `${count} items`} added</strong>
                <span>{cartValueLabel}</span>
              </div>
            </div>
            <div className="cart-bar-cta">
              <span>View Cart</span>
              <Icon name="arrow" size={18} strokeWidth={2} />
            </div>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Drawer + backdrop */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              className="cart-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />
            <motion.aside
              key="drawer"
              className="cart-drawer"
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="cart-title"
              aria-describedby="cart-summary"
              initial={{ x: '104%' }}
              animate={{ x: 0 }}
              exit={{ x: '104%' }}
              transition={drawerSpring}
            >
              <header className="cart-head">
                {checkoutStep && (
                  <button type="button" className="cart-step-back" onClick={() => setCheckoutStep(false)} aria-label="Back to cart">
                    <Icon name="arrow" size={18} strokeWidth={2} />
                  </button>
                )}
                <div className="cart-head-copy">
                  <h3 id="cart-title">{checkoutStep ? 'Order details' : 'Your Order'}</h3>
                  <span id="cart-summary">
                    {checkoutStep
                      ? 'Step 2 of 2 - confirm on WhatsApp'
                      : (count > 0 ? `${count} item${count > 1 ? 's' : ''} selected` : 'Your cart is empty')}
                  </span>
                </div>
                <button type="button" className="cart-close" ref={closeRef} onClick={() => setOpen(false)} aria-label="Close cart">×</button>
              </header>

              <div className="cart-body">
                <p className="cart-live" aria-live="polite">
                  {count > 0 ? `${count} item${count === 1 ? '' : 's'} in your order.` : 'Your order is empty.'}
                </p>
                {checkoutStep ? (
                  <form className="cart-checkout-form" onSubmit={checkoutCart} noValidate>
                    <div className="cart-checkout-recap">
                      <span>{count} {count === 1 ? 'item' : 'items'}</span>
                      <strong>{formatCartValue(cartSummary)}</strong>
                    </div>

                    <div className="cart-form-row">
                      <label htmlFor="cart-name">
                        <span>Name *</span>
                        <input
                          name="name"
                          id="cart-name"
                          value={checkoutDetails.name}
                          onChange={updateCheckout}
                          autoComplete="name"
                          placeholder="Your name"
                          className={checkoutErrors.name ? 'has-error' : ''}
                          aria-invalid={Boolean(checkoutErrors.name)}
                          aria-describedby={checkoutErrors.name ? 'cart-name-error' : undefined}
                          required
                          minLength="2"
                        />
                        {checkoutErrors.name && <small id="cart-name-error" className="cart-field-error" role="alert">{checkoutErrors.name}</small>}
                      </label>
                      <label htmlFor="cart-phone">
                        <span>Phone *</span>
                        <input
                          name="phone"
                          id="cart-phone"
                          type="tel"
                          inputMode="numeric"
                          value={checkoutDetails.phone}
                          onChange={updateCheckout}
                          autoComplete="tel"
                          placeholder="98765 43210"
                          maxLength="16"
                          className={checkoutErrors.phone ? 'has-error' : ''}
                          aria-invalid={Boolean(checkoutErrors.phone)}
                          aria-describedby={checkoutErrors.phone ? 'cart-phone-error' : undefined}
                          required
                        />
                        {checkoutErrors.phone && <small id="cart-phone-error" className="cart-field-error" role="alert">{checkoutErrors.phone}</small>}
                      </label>
                    </div>

                    <fieldset className="cart-fulfillment">
                      <legend>How would you like your order?</legend>
                      <div className="cart-segment">
                        <label className={checkoutDetails.fulfillment === 'delivery' ? 'active' : ''}>
                          <input type="radio" name="fulfillment" value="delivery" checked={checkoutDetails.fulfillment === 'delivery'} onChange={updateCheckout} />
                          <span>Delivery</span>
                        </label>
                        <label className={checkoutDetails.fulfillment === 'pickup' ? 'active' : ''}>
                          <input type="radio" name="fulfillment" value="pickup" checked={checkoutDetails.fulfillment === 'pickup'} onChange={updateCheckout} />
                          <span>Pickup</span>
                        </label>
                      </div>
                    </fieldset>

                    {checkoutDetails.fulfillment === 'delivery' && (
                      <>
                        <label htmlFor="cart-area">
                          <span>Delivery area *</span>
                          <input
                            name="area"
                            id="cart-area"
                            value={checkoutDetails.area}
                            onChange={updateCheckout}
                            placeholder={siteContent.contact.deliveryAreas.slice(0, 2).join(', ') || 'Your area'}
                            className={checkoutErrors.area ? 'has-error' : ''}
                            aria-invalid={Boolean(checkoutErrors.area)}
                            aria-describedby={checkoutErrors.area ? 'cart-area-error' : undefined}
                            required
                          />
                          {checkoutErrors.area && <small id="cart-area-error" className="cart-field-error" role="alert">{checkoutErrors.area}</small>}
                        </label>
                        <label htmlFor="cart-address">
                          <span>Full address *</span>
                          <textarea
                            name="address"
                            id="cart-address"
                            value={checkoutDetails.address}
                            onChange={updateCheckout}
                            autoComplete="street-address"
                            placeholder="Flat, building, street and landmark"
                            maxLength="220"
                            className={checkoutErrors.address ? 'has-error' : ''}
                            aria-invalid={Boolean(checkoutErrors.address)}
                            aria-describedby={checkoutErrors.address ? 'cart-address-error' : undefined}
                            required
                          />
                          {checkoutErrors.address && <small id="cart-address-error" className="cart-field-error" role="alert">{checkoutErrors.address}</small>}
                        </label>
                      </>
                    )}

                    <div className="cart-form-row">
                      <label htmlFor="cart-date">
                        <span>Needed by</span>
                        <input
                          type="date"
                          name="date"
                          id="cart-date"
                          value={checkoutDetails.date}
                          min={earliestDate}
                          onChange={updateCheckout}
                          className={checkoutErrors.date ? 'has-error' : ''}
                          aria-invalid={Boolean(checkoutErrors.date)}
                          aria-describedby={checkoutErrors.date ? 'cart-date-error' : 'cart-date-hint'}
                        />
                        <small id="cart-date-hint" className="cart-field-hint cart-field-hint-left">Earliest available date: {earliestDate}</small>
                        {checkoutErrors.date && <small id="cart-date-error" className="cart-field-error" role="alert">{checkoutErrors.date}</small>}
                      </label>
                      <label htmlFor="cart-time">
                        <span>Preferred time</span>
                        <select id="cart-time" name="time" value={checkoutDetails.time} onChange={updateCheckout}>
                          {siteContent.ordering.timeSlots.map((slot) => <option key={slot}>{slot}</option>)}
                        </select>
                      </label>
                    </div>

                    <label className="cart-checkbox" htmlFor="cart-eggless">
                      <input id="cart-eggless" type="checkbox" name="eggless" checked={checkoutDetails.eggless} onChange={updateCheckout} />
                      <span>Make it eggless where available</span>
                    </label>

                    <label htmlFor="cart-notes">
                      <span>Notes or cake message</span>
                      <textarea
                        name="notes"
                        id="cart-notes"
                        value={checkoutDetails.notes}
                        onChange={updateCheckout}
                        placeholder="Flavour, custom text, allergies, landmark..."
                        maxLength="300"
                        aria-describedby="cart-notes-hint"
                      />
                      <small id="cart-notes-hint" className="cart-field-hint">{siteContent.ordering.allergyNotice} · {checkoutDetails.notes.length}/300</small>
                    </label>

                    <details className="cart-message-preview">
                      <summary>Review WhatsApp message</summary>
                      <pre>{checkoutMessage}</pre>
                    </details>

                    <p role="status" className="cart-helper">{enquiryStatus}</p>
                    <button type="submit" className="cart-checkout cart-checkout-final">
                      <Icon name="whatsapp" size={18} />
                      <span>Send order on WhatsApp</span>
                    </button>
                    <p className="cart-confirm-note">{siteContent.ordering.confirmationNote}</p>
                    {checkoutFallback && (
                      <a className="cart-whatsapp-fallback" href={checkoutFallback} target="_blank" rel="noreferrer">
                        WhatsApp did not open. Tap here to continue.
                      </a>
                    )}
                  </form>
                ) : detailed.length === 0 ? (
                  <motion.div
                    className="cart-empty"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 }}
                  >
                    <div className="cart-empty-mark"><Icon name="gift" size={34} strokeWidth={1.4} /></div>
                    <p>Nothing here yet.</p>
                    <span>Add a few bakes from the menu and they'll show up here.</span>
                    <button type="button" className="cart-empty-btn" ref={emptyActionRef} onClick={() => setOpen(false)}>Browse the menu</button>
                  </motion.div>
                ) : (
                  <AnimatePresence initial={false}>
                    {detailed.map(({ key, product, qty, lineTotal }) => (
                      <motion.div
                        className="cart-item"
                        key={key}
                        layout
                        initial={{ opacity: 0, x: 32 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 48 }}
                        transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                      >
                        <img src={product.image} alt={product.name} className="cart-item-img" loading="lazy" decoding="async" />
                        <div className="cart-item-info">
                          <div className="cart-item-top">
                            <h4>{product.name}</h4>
                            <button type="button" className="cart-item-remove" onClick={() => remove(product.id, product.variantId)} aria-label={`Remove ${product.name} from order`}>×</button>
                          </div>
                          <span className="cart-item-unit">{product.price == null ? 'Price confirmed on WhatsApp' : `${formatINR(product.price)} each`}</span>
                          <div className="cart-item-bottom">
                            <div className="stepper" role="group" aria-label={`Quantity for ${product.name}`}>
                              <button type="button" onClick={() => decrement(product.id, product.variantId)} aria-label={`Decrease ${product.name} quantity`}>−</button>
                              <motion.span
                                key={qty}
                                aria-label={`Quantity ${qty}`}
                                initial={{ y: -8, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 24 }}
                              >
                                {qty}
                              </motion.span>
                              <button
                                type="button"
                                onClick={() => add(product.id, product.variantId)}
                                aria-label={`Increase ${product.name} quantity`}
                                disabled={qty >= siteContent.ordering.maxQuantity}
                              >+</button>
                            </div>
                            <strong className="cart-item-total">{product.price == null ? 'On request' : formatINR(lineTotal)}</strong>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>

              {detailed.length > 0 && !checkoutStep && (
                <footer className="cart-foot">
                  <div className="cart-foot-row">
                    <span>{hasQuoteItems ? (cartSummary.pricedTotal > 0 ? 'Priced items subtotal' : 'Pricing') : 'Estimated total'}</span>
                    <strong>{formatCartValue(cartSummary)}</strong>
                  </div>
                  <p className="cart-foot-note">
                    {hasQuoteItems && 'Items marked “on request” are not included above. '}
                    {siteContent.ordering.paymentNote}
                  </p>
                  <motion.button
                    type="button"
                    className="cart-checkout"
                    onClick={() => {
                      setCheckoutErrors({});
                      setCheckoutFallback('');
                      setCheckoutStep(true);
                    }}
                    aria-label="Add delivery details and continue to WhatsApp"
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: 'spring', stiffness: 360, damping: 20 }}
                  >
                    <Icon name="whatsapp" size={18} />
                    <span>Add details & continue</span>
                  </motion.button>
                  <p className="cart-confirm-note">Next: delivery or pickup details, then WhatsApp.</p>
                  {confirmClear ? (
                    <div className="cart-clear-confirm" role="group" aria-label="Confirm clear order">
                      <span>Remove every item?</span>
                      <button
                        type="button"
                        ref={keepItemsRef}
                        onClick={() => {
                          setConfirmClear(false);
                          window.requestAnimationFrame(() => clearTriggerRef.current?.focus());
                        }}
                      >
                        Keep items
                      </button>
                      <button type="button" className="cart-clear-danger" onClick={clearCart}>Yes, clear</button>
                    </div>
                  ) : (
                    <button type="button" className="cart-clear" ref={clearTriggerRef} onClick={() => setConfirmClear(true)}>Clear order</button>
                  )}
                </footer>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
export default Cart;
